/**
 * Making a word audible.
 *
 * Three sources, in order, and the order is the whole design
 * ([D-013](../../../docs/DECISIONS.md#d-013)):
 *
 * 1. **The recording**, when the course has one. One voice, one pace, chosen by
 *    someone who can hear the difference between /θ/ and /s/.
 * 2. **The device's own voice**, when it does not. D-013 already sanctions this
 *    as the fallback, and it is a real English voice from the platform rather
 *    than something this app synthesised.
 * 3. **Nothing**, and the exercise renders the fallback its author wrote.
 *
 * The choice is made **per word, at the moment a child taps**, not once per
 * lesson. That matters: the course is part-recorded for a long time, and a
 * lesson-wide decision would either hide the exercises that do have audio or
 * strand a child on the ones that do not.
 *
 * Nothing here throws. A word that will not play is a word the child reads.
 */

import { learningNow } from './language.js';

/** Where the server mounts the course's assets. Matches `ASSET_PATH`. */
export const ASSET_PATH = '/assets';

export const assetUrl = (ref: string): string => `${ASSET_PATH}/${ref}`;

/**
 * The language the course teaches, which voices are matched against: the
 * child's course, English until the app knows otherwise
 * ([D-189](../../../docs/DECISIONS.md#d-189)).
 */
export const spokenLanguage = (): string => learningNow().l2;

/**
 * Whether the course has recordings in this deployment.
 *
 * It does: every line the course says has a clip, in the voices the owner
 * chose — Alice, Sneakers' voice, for everything for now
 * ([D-181](../../../docs/DECISIONS.md#d-181)), British throughout
 * ([D-120](../../../docs/DECISIONS.md#d-120),
 * [D-146](../../../docs/DECISIONS.md#d-146)) — and the gate fails if one is
 * missing. So it is **on unless a deployment turns it off** with
 * `VITE_HAS_RECORDINGS=false`, which a deploy without the audio files would
 * want: then the whole course falls through to the platform voice
 * ([D-064](../../../docs/DECISIONS.md#d-064)). A clip that will not play falls
 * through to the voice as well, one line at a time, so a child is never left
 * with silence.
 *
 * It is read from the environment rather than fixed in this file because it is
 * a property of *where the app is running*, and because a source constant that
 * can only ever hold one value in the test suite is a branch nobody can prove.
 */
export const hasRecordings = (): boolean =>
  import.meta.env['VITE_HAS_RECORDINGS'] !== 'false';

/** The `speechSynthesis` this device offers, or null where there is none. */
const synth = (): SpeechSynthesis | null =>
  'speechSynthesis' in globalThis ? globalThis.speechSynthesis : null;

/**
 * A voice from the platform in the course's language, if it has one.
 *
 * A Danish child's iPad is set to Danish, so the *default* voice is the wrong
 * language — reading `hello` in Danish teaches a Danish accent, which is the one
 * thing this exercise exists to correct. So a voice is chosen by language and
 * the default is never assumed.
 */
export function courseVoice(
  lang: string = spokenLanguage(),
): SpeechSynthesisVoice | null {
  const voices = (synth()?.getVoices() ?? []).filter((v) =>
    v.lang.toLowerCase().startsWith(lang),
  );
  // The course speaks in one voice, Sneakers' — a woman's, and British in
  // English ([D-181](../../../docs/DECISIONS.md#d-181)). The device's voice
  // only says what was never recorded, so it should sound as much like her as
  // the device allows: a woman from the course's region, then **a woman from
  // anywhere the language is spoken, before a man from the right region**
  // ([D-195](../../../docs/DECISIONS.md#d-195)). An iPad's one British voice
  // out of the box is Daniel, and taking him first gave Sneakers a man's voice
  // on every line the recordings did not cover.
  const preferred = PREFERRED[lang];
  const regional =
    preferred === undefined ? [] : voices.filter((v) => preferred.region.test(v.lang));
  const women = preferred?.women ?? WOMEN;
  return (
    regional.find((v) => women.test(v.name)) ??
    voices.find((v) => women.test(v.name)) ??
    regional[0] ??
    voices[0] ??
    null
  );
}

/** Any platform that says so in the name. */
const WOMEN = /\bfemale\b/i;

/**
 * Per language: the region the course speaks, and the names platforms give the
 * women's voices there.
 *
 * English is British: Apple's Kate, Serena, Martha and Stephanie; Chrome's
 * "Google UK English Female"; Microsoft's Hazel, Susan, Libby, Sonia and
 * Maisie. Where a device has none of those, the women it has in other English:
 * Apple's Samantha, Karen, Moira, Tessa, Fiona, Victoria, Allison, Ava, Zoe and
 * Nicky; Microsoft's Zira, Aria and Jenny. Swedish is Sweden's: Apple's Alva
 * and Klara, Microsoft's Sofie and Hillevi.
 */
const PREFERRED: Readonly<Record<string, { region: RegExp; women: RegExp }>> = {
  en: {
    region: /^en[-_]gb/i,
    women:
      /\b(female|kate|serena|martha|stephanie|hazel|susan|libby|sonia|maisie|samantha|karen|moira|tessa|fiona|victoria|allison|ava|zoe|nicky|zira|aria|jenny)\b/i,
  },
  sv: {
    region: /^sv[-_]se/i,
    women: /\b(female|alva|klara|sofie|hillevi)\b/i,
  },
};

/**
 * Whether this device can make a word in the course's language audible at all.
 *
 * This is what `capabilities.audio` is built from, so it is the answer to a
 * precise question: *if the server sends a listening exercise, can this child
 * hear the word?* Anything less certain would be the app claiming a capability
 * and then not having it, which is how a child ends up with a question they
 * cannot answer.
 */
export const canSpeak = (): boolean => hasRecordings() || courseVoice() !== null;

/**
 * How fast "slower" is, for a recording and for the device's voice.
 *
 * The recording used to ignore it: the device voice slowed to 0.6, but every
 * line in the course has a clip and the clip always played at full speed — so
 * the 🐢 button said the word exactly as the 🔊 one did. Pitch is kept, so a
 * slow word is the same voice taking its time rather than a deeper one.
 */
export const SLOW_RATE = 0.6;

/** The recording playing now, so a new tap or a screen change can stop it. */
let playing: HTMLAudioElement | null = null;

const stopRecording = (): void => {
  if (playing === null) return;
  playing.pause();
  playing = null;
};

/**
 * The one element every recording plays through.
 *
 * One, not one per line, because of the iPad ([D-195](../../../docs/DECISIONS.md#d-195)).
 * Safari plays a sound only when it starts inside a tap — and Sneakers speaks
 * the moment a screen has *loaded*, a network round trip after the tap that
 * opened it. A fresh element for each line was refused every time, so every
 * line he said fell through to the device's voice: not Alice, and on an iPad
 * a man's. An element that has once played inside a tap may play again
 * without one, so this one is played, silently, on the first tap there is
 * ({@link unlockVoice}), and everything after goes through it.
 */
let player: HTMLAudioElement | null = null;
const voicePlayer = (): HTMLAudioElement | null => {
  if (typeof Audio === 'undefined') return null;
  // `instanceof`, so a test that swaps `Audio` gets a player of its own.
  if (player === null || !(player instanceof Audio)) player = new Audio();
  return player;
};

/** A real, short file on this origin, played muted: `media-src 'self'` allows nothing else. */
const UNLOCK_REF = 'audio/ui/tap.mp3';

/**
 * Let recordings play on Safari: call from inside the first tap.
 *
 * Plays a short sound through the shared element with the sound off, then
 * stops it. Nothing is heard, and from then on Sneakers' lines play as
 * recordings rather than in the device's voice.
 */
export function unlockVoice(): void {
  if (!hasRecordings()) return;
  const element = voicePlayer();
  if (element === null) return;
  const url = assetUrl(UNLOCK_REF);
  element.muted = true;
  element.src = url;
  void element
    .play()
    .then(() => {
      // Only if nothing real has started on it in the meantime.
      if (element.src.endsWith(url)) element.pause();
    })
    .catch(() => undefined)
    .finally(() => {
      element.muted = false;
    });
}

/**
 * Stop the device's voice, but only if it is saying something.
 *
 * Safari, asked to `cancel()` with nothing speaking and then to `speak()`
 * straight away, can blurt the start of the cancelled queue — a stray sound
 * in front of the line ([D-195](../../../docs/DECISIONS.md#d-195)).
 */
const quietVoice = (): void => {
  const speech = synth();
  if (speech !== null && (speech.speaking || speech.pending)) speech.cancel();
};

/** Play a recording. Resolves false if there is not one, or it will not play. */
async function playRecording(ref: string, slow = false): Promise<boolean> {
  // Nobody has recorded the English yet, and every reference in the course
  // points at a file that is not there — so asking for one is a request that is
  // *known* to 404 before it is sent. That was survivable while the only way to
  // trigger it was a child tapping "Hear it". Sneakers speaks the moment a
  // lesson opens, without being asked, so it became a guaranteed failed request
  // on every single lesson: wasted on school wifi, and a red line in the
  // console that hides the errors worth reading.
  //
  // `hasRecordings()` is exactly this fact about the deployment, and `canSpeak`
  // already consults it. This makes the playing path agree with the answer the
  // capability check has been giving all along.
  if (!hasRecordings()) return false;
  const audio = voicePlayer();
  if (audio === null) return false;
  // A second tap means "say it now", not "say it twice, overlapping".
  stopRecording();
  quietVoice();
  audio.muted = false;
  audio.src = assetUrl(ref);
  // Both rates: loading a new source resets `playbackRate` to the default.
  const rate = slow ? SLOW_RATE : 1;
  audio.defaultPlaybackRate = rate;
  audio.playbackRate = rate;
  audio.preservesPitch = true;
  playing = audio;
  try {
    await audio.play();
    return true;
  } catch {
    // A 404 because nobody has recorded it, a codec the browser will not take,
    // or an autoplay policy. All three mean the same thing here: use the voice.
    if (playing === audio) playing = null;
    return false;
  }
}

/**
 * How far through the line the voice is, 0–1, or null when nothing is speaking.
 *
 * A module variable with subscribers rather than a return value, because the
 * thing that wants it — Sneakers' mouth — is nowhere near the thing that starts
 * the speaking. Everything here is best effort: a browser that fires no
 * boundary events still gets a moving mouth from the estimate below.
 */
let progress: number | null = null;
const watchers = new Set<(at: number | null) => void>();

const announce = (at: number | null): void => {
  progress = at;
  for (const watcher of watchers) watcher(at);
};

/** Watch the voice. Returns the unsubscribe. */
export function whileSpeaking(watcher: (at: number | null) => void): () => void {
  watchers.add(watcher);
  watcher(progress);
  return () => {
    watchers.delete(watcher);
  };
}

/**
 * Roughly how long this will take to say, in milliseconds.
 *
 * Characters rather than words, because a long word takes longer than a short
 * one and `speechSynthesis` will not tell us anything until it starts. ~14
 * characters a second at rate 1 is close enough for a mouth; it is corrected by
 * every boundary event that does arrive, and it is never used to decide anything
 * a child would notice if it were wrong.
 */
const estimate = (text: string, rate: number): number =>
  Math.max(400, (text.length / 14) * 1000) / rate;

/** Speak with the device's own voice. Resolves false if it has no voice in that language. */
function speak(text: string, slow: boolean, lang: string): boolean {
  const speech = synth();
  const voice = courseVoice(lang);
  if (speech === null || voice === null) return false;

  // A second utterance while one is in flight queues behind it, so a child
  // tapping twice would hear the word twice, late. Cancelling first makes the
  // tap mean "say it now".
  quietVoice();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.voice = voice;
  utterance.lang = voice.lang;
  // Slow is for the "hear it slower" tap, and it is a real accessibility
  // feature rather than a gimmick ([Accessibility](../../../docs/ACCESSIBILITY.md)).
  const rate = slow ? SLOW_RATE : 0.95;
  utterance.rate = rate;

  // The mouth. Driven by a clock that boundary events correct where a browser
  // sends them, and that runs on its own where one does not — Safari has been
  // unreliable about `onboundary` for years, and a mouth that only moves in
  // Chrome is not a feature.
  const began = Date.now();
  const expected = estimate(text, rate);
  let timer: ReturnType<typeof setInterval> | null = null;
  const stop = () => {
    if (timer !== null) clearInterval(timer);
    timer = null;
    announce(null);
  };
  timer = setInterval(() => {
    const at = (Date.now() - began) / expected;
    if (at >= 1) stop();
    else announce(at);
  }, 55);

  utterance.onboundary = (event) => {
    // The one hard number a browser gives: how far into the text it has read.
    if (text.length > 0) announce(Math.min(0.999, event.charIndex / text.length));
  };
  utterance.onend = stop;
  utterance.onerror = stop;

  /*
    Announce the start *now*, not on the first tick.

    The interval above runs every 55 ms, so without this the app believes
    nobody is talking for the first 55 ms of every line — and the feedback cue
    is required to land within 50 ms of a tap
    ([Sound](../../../docs/SOUND.md)). The two windows overlap exactly, so an
    effect fired the instant Sneakers begins would play over him: the one thing
    the volume discipline forbids.

    It moves the mouth sooner too, which is the smaller half of the reason.
  */
  announce(0);
  speech.speak(utterance);
  return true;
}

export interface SaidWhat {
  /** Which source produced the sound, or `none` if nothing did. */
  readonly from: 'recording' | 'voice' | 'none';
}

/**
 * Say a word.
 *
 * `ref` is the recording to prefer; `text` is what to fall back to speaking. A
 * caller that has one but not the other passes null for the missing half.
 */
export async function say(
  ref: string | null,
  text: string | null,
  options: {
    readonly slow?: boolean;
    /**
     * The language `text` is in, when it is not the course's. Sneakers'
     * own lines are the app talking, which is English in every course
     * ([D-068](../../../docs/DECISIONS.md#d-068)), so they pass `en` — a
     * Swedish voice reading an English sentence is nobody's voice.
     */
    readonly language?: string;
  } = {},
): Promise<SaidWhat> {
  if (ref !== null && (await playRecording(ref, options.slow ?? false))) {
    return { from: 'recording' };
  }
  const lang = options.language ?? spokenLanguage();
  if (text !== null && speak(text, options.slow ?? false, lang))
    return { from: 'voice' };
  return { from: 'none' };
}

/** Stop whatever is being said. Used when a lesson screen goes away. */
export function hush(): void {
  stopRecording();
  synth()?.cancel();
  // `cancel()` does not reliably fire `onend`, and a mouth left mid-vowel
  // because a child changed screens is exactly the stunned look this is for.
  announce(null);
}
